import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { theme } from '../theme';
import { BentoCard } from './shared';
import {
  listUsers, addUser, deleteUser, updateUserRoles,
  getCustomerConfig, updateCustomerConfig, UserEntry, UserRoles,
} from '../services/api';
import { formatBackendError } from '../utils/formatError';
import { usePaletteMode } from '../context/PaletteModeContext';
import { useListDetailFlip } from '../context/ListDetailFlipContext';
import { defaultRowLayout, useCategoryMeta } from '../context/ClassificationSchemaContext';
import AntwortKatalog from './AntwortKatalog';
import TexteUndVokabular from './TexteUndVokabular';
import RoutingVerwaltung from './RoutingVerwaltung';
import {
  KnownRole, MSB_ROLES, VNB_ONLY, ownsActsAndRouting, hostsTextOrVocabularyEditing,
} from '../services/roleRegistry';

// <purpose>Admin settings page — user management (admin only) + company config + workflow toggles (admin+customeradmin).</purpose>
// <dependencies>Admin API endpoints (/api/admin/*), BentoCard, theme.ts</dependencies>

interface EinstellungenProps {
  role: string;
  userRoles: UserRoles | null;
  // Notify App-level AppConfig holder so autoArchiveDays/uiWarps propagate to other
  // pages without requiring a full reload.
  onConfigSaved?: () => void;
}

// UI_WARP_* keys editable from the "Workflow-Verhalten" section.
// Server defaults seeded by migration 037; FE displays a toggle per key.
// All 4 labels follow the harmonized voice per Spec §5 (2026-05-27):
//   Label:       Nach {Trigger-Aktion} {Ziel-Aktion}
//   Description: Wenn aktiv, ...
// DGUV Ch.8 Law of Similarity — same-function elements should look alike.
type WorkflowToggle = { key: string; label: string; description: string };
export const WORKFLOW_TOGGLES: WorkflowToggle[] = [
  { key: 'UI_WARP_APPROVE_TO_GENEHMIGT',
    label: 'Nach Genehmigen zum „Genehmigt"-Tab wechseln',
    description: 'Wenn aktiv, springt die Antworten-Ansicht nach Klick auf Genehmigen direkt in den Genehmigt-Tab.' },
  { key: 'UI_WARP_PROCESS_TO_ANTWORTEN',
    label: 'Nach Verarbeiten zur Antworten-Seite springen',
    description: 'Wenn aktiv, öffnet sich nach Klick auf Verarbeiten in Uploads die Antworten-Seite mit dem Ticket.' },
  { key: 'UI_WARP_TESTGEN_PROCESS_TO_ANTWORTEN',
    label: 'Nach „Direkt verarbeiten" im Testgenerator zur Antworten-Seite springen',
    description: 'Wenn aktiv, springt der Testgenerator nach direkt-verarbeiten zur Antworten-Seite mit dem Ticket.' },
  { key: 'UI_WARP_TESTGEN_UPLOAD_TO_UPLOADS',
    label: 'Nach „Hochladen" im Testgenerator zur Uploads-Seite springen',
    description: 'Wenn aktiv, springt der Testgenerator nach hochladen zur Uploads-Seite mit dem Ticket.' },
];

type ConfigKey = {
  key: string;
  label: string;
  roles?: readonly KnownRole[];
  // NEW — for non-text controls. Default is text input when type is undefined.
  type?: 'text' | 'radio';
  description?: string;
  options?: Array<{ value: string; label: string; isDefault?: boolean }>;
};
type ConfigGroup = { label: string; keys: ConfigKey[] };

// CONFIG_GROUPS statically declares the customer-config form schema per role.
// The BE PUT gate at /manage/config validates against each customer's
// prompt-derived requiredKeys set (see smarttix-core.md § Data isolation), so
// stale markers here cannot cause data drift — BE rejects out-of-scope keys
// regardless. Replacing static markers with a BE-driven dynamic schema endpoint
// is a planned follow-up.
//
// roles[] = shown only for listed roles. Omitted = all roles.
// MSB-only keys (6 total) carry roles: MSB_ROLES: CUSTOMER_DOMAIN, CUSTOMER_PRICE_SHEET_URL,
// CUSTOMER_METER_ORDER_URL, CUSTOMER_METER_COUNT, CUSTOMER_GATEWAY_VENDOR, CUSTOMER_CERTIFIED_ENTITY.
// Empirically validated against smarttix_template active prompts on 2026-05-04.
const CONFIG_GROUPS: ConfigGroup[] = [
  { label: 'Unternehmen', keys: [
    { key: 'CUSTOMER_NAME', label: 'Firmenname' },
    { key: 'CUSTOMER_LEGAL_NAME', label: 'Juristischer Name' },
    { key: 'CUSTOMER_DEPARTMENT', label: 'Abteilung' },
    { key: 'CUSTOMER_DOMAIN', label: 'Domain', roles: MSB_ROLES },
  ]},
  { label: 'Kontakt', keys: [
    { key: 'CUSTOMER_HOTLINE', label: 'Hotline' },
    { key: 'CUSTOMER_CONTACT_URL', label: 'Kontaktformular URL' },
    { key: 'CUSTOMER_PORTAL_NAME', label: 'Kundenportal Name' },
    { key: 'CUSTOMER_PORTAL_URL', label: 'Kundenportal URL' },
    { key: 'CUSTOMER_DSB_CONTACT', label: 'Datenschutz-Kontakt (DSGVO-Weiterleitung)', roles: VNB_ONLY },
  ]},
  // Own group rather than folded into Technisch (which is MSB-metering-hardware
  // themed, unrelated) — these are VNB-only emergency contact numbers, closer in
  // kind to Kontakt but role-restricted, so they get their own cluster.
  { label: 'Notfall', keys: [
    { key: 'NOTFALL_STOERUNGSNUMMER', label: 'Störungsnummer (Notfall Strom)', roles: VNB_ONLY },
    { key: 'NOTFALL_GAS_HOTLINE', label: 'Gas-Notruf-Hotline (Notfall Gas)', roles: VNB_ONLY },
  ]},
  { label: 'Links', keys: [
    { key: 'CUSTOMER_PRICE_URL', label: 'Preisblatt URL' },
    { key: 'CUSTOMER_PRICE_SHEET_URL', label: 'Netzentgelte URL', roles: MSB_ROLES },
    { key: 'CUSTOMER_OUTAGE_URL', label: 'Störungen URL' },
    { key: 'CUSTOMER_INFO_URL', label: 'Service-Informationen URL' },
    { key: 'CUSTOMER_METER_ORDER_URL', label: 'Zählerwechsel URL', roles: MSB_ROLES },
  ]},
  { label: 'Technisch', keys: [
    { key: 'CUSTOMER_METER_COUNT', label: 'Zählerbestand', roles: MSB_ROLES },
    { key: 'CUSTOMER_GATEWAY_VENDOR', label: 'Gateway-Hersteller', roles: MSB_ROLES },
    { key: 'CUSTOMER_CERTIFIED_ENTITY', label: 'Zertifiziertes Unternehmen', roles: MSB_ROLES },
  ]},
  { label: 'Rechtlich', keys: [
    { key: 'CUSTOMER_ADDRESS', label: 'Anschrift' },
    { key: 'CUSTOMER_MANAGEMENT', label: 'Geschäftsführung' },
    { key: 'CUSTOMER_HQ_CITY', label: 'Sitz' },
    { key: 'CUSTOMER_COURT', label: 'Registergericht' },
    { key: 'CUSTOMER_TRADE_REG', label: 'Handelsregister' },
    { key: 'CUSTOMER_VAT_ID', label: 'USt-IdNr.' },
  ]},
  // The Statistik group stays in CONFIG_GROUPS so the dedicated "Statistik" card
  // (statistikLane) can lookup the entry by label. But it's filtered OUT of
  // the Firmeninformationen render loop below (see CONFIG_GROUPS.filter call) so
  // it doesn't appear as a duplicate inside the company-info save-lane.
  { label: 'Statistik', keys: [
    {
      key: 'HEADLINE_RATE_MODE',
      label: 'Antwortsicherheits-Modus',
      type: 'radio',
      description: 'Bestimmt, ob Vorschläge, die noch auf Genehmigung durch den Sachbearbeiter warten, in die Antwortsicherheit einfließen. Der zweite Modus zeigt deutlicher, dass ausstehende Vorschläge die Quote drücken — sinnvoll, wenn Sachbearbeiter zur zügigen Bearbeitung motiviert werden sollen.',
      options: [
        { value: 'quality_only', label: 'Nur erfolgreich bewertete Tickets', isDefault: true },
        { value: 'with_pending', label: 'Inkl. ausstehende Vorschläge' },
      ],
    },
  ]},
];

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px', fontSize: 13,
  border: `1px solid ${theme.colors.borderLight}`, borderRadius: theme.borderRadius.sm,
  fontFamily: theme.font.family, outline: 'none', boxSizing: 'border-box',
};

const btnStyle = (variant: 'primary' | 'danger' | 'ghost'): React.CSSProperties => ({
  padding: '6px 14px', fontSize: 12, fontWeight: 600, fontFamily: theme.font.family,
  border: variant === 'ghost' ? `1px solid ${theme.colors.borderLight}` : 'none',
  borderRadius: theme.borderRadius.sm, cursor: 'pointer',
  color: variant === 'danger' ? theme.colors.error : variant === 'primary' ? theme.colors.white : theme.colors.navyMid,
  backgroundColor: variant === 'primary' ? theme.colors.navy : variant === 'danger' ? '#fef2f2' : theme.colors.white,
});

const badgeStyle = (isAdmin: boolean): React.CSSProperties => ({
  fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
  color: isAdmin ? theme.colors.white : theme.colors.navyMid,
  backgroundColor: isAdmin ? theme.colors.navy : theme.colors.bgLight,
});

const RadioGroup: React.FC<{
  name: string;
  options: Array<{ value: string; label: string; isDefault?: boolean }>;
  value: string;
  onChange: (v: string) => void;
}> = ({ name, options, value, onChange }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    {options.map(opt => (
      <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontFamily: theme.font.family, color: theme.colors.navy, cursor: 'pointer' }}>
        <input
          type="radio"
          name={name}
          value={opt.value}
          checked={value === opt.value}
          onChange={() => onChange(opt.value)}
          style={{ cursor: 'pointer' }}
        />
        <span>{opt.label}{opt.isDefault ? ' (Standard)' : ''}</span>
      </label>
    ))}
  </div>
);

// Per-user light/dark palette preference selector (FE-only, localStorage via usePaletteMode).
// Segmented Hell|Dunkel pill pair; mirrors btnStyle's pill voice but anchors active to navy.
export const DarstellungToggle: React.FC = () => {
  const { mode, setMode } = usePaletteMode();
  const btn = (active: boolean): React.CSSProperties => ({
    padding: '6px 14px', fontSize: 13, fontWeight: active ? 600 : 400, cursor: 'pointer',
    color: active ? theme.colors.white : theme.colors.navyMuted,
    background: active ? theme.colors.navy : 'transparent',
    border: `1px solid ${active ? theme.colors.navy : theme.colors.borderLight}`,
    borderRadius: 20, outline: 'none', fontFamily: theme.font.family,
  });
  return (
    <div role="group" aria-label="Darstellung" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <button aria-pressed={mode === 'light'} onClick={() => setMode('light')} style={btn(mode === 'light')}>Hell</button>
      <button aria-pressed={mode === 'dark'} onClick={() => setMode('dark')} style={btn(mode === 'dark')}>Dunkel</button>
    </div>
  );
};

// Per-user list/detail morph preference (FE-only, localStorage via useListDetailFlip).
// Segmented Aus|Ein pill pair; mirrors DarstellungToggle shape exactly.
export const ListDetailFlipToggle: React.FC = () => {
  const { flip, setFlip } = useListDetailFlip();
  const btn = (active: boolean): React.CSSProperties => ({
    padding: '6px 14px', fontSize: 13, fontWeight: active ? 600 : 400, cursor: 'pointer',
    color: active ? theme.colors.white : theme.colors.navyMuted,
    background: active ? theme.colors.navy : 'transparent',
    border: `1px solid ${active ? theme.colors.navy : theme.colors.borderLight}`,
    borderRadius: 20, outline: 'none', fontFamily: theme.font.family,
  });
  return (
    <div role="group" aria-label="Detailansicht vergrößern bei Auswahl" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <button aria-pressed={!flip} onClick={() => setFlip(false)} style={btn(!flip)}>Aus</button>
      <button aria-pressed={flip} onClick={() => setFlip(true)} style={btn(flip)}>Ein</button>
    </div>
  );
};

// Spec 2026-05-27 §2 + §7 — 4 task-named tabs. Order = daily-visit-frequency, leftmost most visited.
type TabKey = 'workflow' | 'firma' | 'statistik' | 'team' | 'texte' | 'katalog' | 'routing';

// Two independent gates per tab, deliberately not collapsed into one predicate:
//   visibleFor   — what this USER may do (their role flags on this tenant).
//   availableFor — what this TENANT owns (a capability derived from the backend's per-role
//                  contract projection, roleRegistry.ROLE_CONTRACTS). Omitted = every tenant.
// availableFor replaced an earlier `roles?: readonly KnownRole[]` list: a tab is offered because
// the data behind it EXISTS for this tenant's contract, and spelling that as a role list made the
// reason unreadable and put role names at the gate. The predicates live in roleRegistry, the one
// place allowed to know role names at all.
const TAB_DEFS: Array<{ key: TabKey; label: string; visibleFor: (r: UserRoles) => boolean; availableFor?: (role: string) => boolean }> = [
  { key: 'workflow',  label: 'Workflow',         visibleFor: () => true },
  { key: 'firma',     label: 'Firmendaten',      visibleFor: () => true },
  { key: 'statistik', label: 'Statistik-Modus',  visibleFor: r => !!(r.isAdmin || r.isCustomerAdmin) },
  { key: 'team',      label: 'Team & Zugriff',   visibleFor: r => !!r.isAdmin },
  // The generalized editing tab: reply-prose section + vocabulary section, each gated on its own
  // capability inside (see TexteUndVokabular). Offered when the tenant owns EITHER — a tenant that
  // owns neither must never get an empty shell.
  // ⚠ This tab used to read "Antwort-Texte, vnb-only, because the reply engine's editable
  // components are a VNB-role concept". That claim is STALE and was corrected here: MSB roles DO
  // carry reply-prompt rows (42, measured 2026-08-11) — none of them customeradmin-editable-safe,
  // which is why MSB gates out of PROSE while gating IN for VOCABULARY. The capability is
  // "carries editable-safe rows", not "is a VNB role"; the full correction lives in
  // roleRegistry.ownsEditableReplyProse.
  { key: 'texte',     label: 'Texte & Vokabular', visibleFor: r => !!r.isAdmin, availableFor: hostsTextOrVocabularyEditing },
  // Read-only mirror of the answer-capability catalog (Task 11 CRUD API). Admin-only for now —
  // CRUD surfacing (and any broader visibility) lands with the Mandantenstimme increment.
  // Gated on the acts/routing capability: vnb_acts is a vnb-CONTRACT data structure with no
  // counterpart under the other contracts, so the tab has nothing to show elsewhere.
  { key: 'katalog',   label: 'Antwort-Katalog',  visibleFor: r => !!r.isAdmin, availableFor: ownsActsAndRouting },
  // Routing-Regel-CRUD (routing_rules, RT8 admin API). Same capability as Antwort-Katalog —
  // routing_rules is the other half of the same vnb-contract structure.
  { key: 'routing',   label: 'Routing',          visibleFor: r => !!r.isAdmin, availableFor: ownsActsAndRouting },
];

// Spec §7 — resolution order: URL ?tab= > localStorage > first visible-to-role.
function resolveInitialTab(visible: TabKey[]): TabKey {
  const fromUrl = new URLSearchParams(window.location.search).get('tab') as TabKey | null;
  if (fromUrl && visible.includes(fromUrl)) return fromUrl;
  const fromStorage = localStorage.getItem('smarttix_einstellungen_tab') as TabKey | null;
  if (fromStorage && visible.includes(fromStorage)) return fromStorage;
  return visible[0];
}

const EinstellungenSeite: React.FC<EinstellungenProps> = ({ role, userRoles, onConfigSaved }) => {
  // Why: User-CRUD section visible only to true admins. Customeradmin sees company-config
  // + workflow toggles but cannot grant roles.
  const canManageUsers = !!userRoles?.isAdmin;
  // Spec §3 — Sachbearbeiter sees AUTO_ARCHIVE_DAYS but cannot edit it; only Kunden-Admin+ edit.
  const canEditAutoArchive = !!(userRoles?.isCustomerAdmin || userRoles?.isAdmin);
  // Spec §3 — Firmendaten editable only by Kunden-Admin+; Sachbearbeiter gets read-only mode.
  const canEditFirma = !!(userRoles?.isCustomerAdmin || userRoles?.isAdmin);

  // Spec §7 tab state — visibleTabs filters TAB_DEFS by role; activeTab resolves
  // from URL → localStorage → first-visible. On change we sync both back.
  const visibleTabs: TabKey[] = useMemo(
    () => userRoles
      ? TAB_DEFS.filter(t => t.visibleFor(userRoles) && (!t.availableFor || t.availableFor(role))).map(t => t.key)
      : ['workflow'],
    [userRoles, role]
  );
  const [activeTab, setActiveTab] = useState<TabKey>(() => resolveInitialTab(visibleTabs));
  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set('tab', activeTab);
    window.history.replaceState({}, '', url.toString());
    localStorage.setItem('smarttix_einstellungen_tab', activeTab);
  }, [activeTab]);
  // ═══ User Management State ═══
  const [users, setUsers] = useState<UserEntry[]>([]);
  const [newEmail, setNewEmail] = useState('');
  const [newIsNormal, setNewIsNormal] = useState(true);
  const [newIsAdmin, setNewIsAdmin] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [userError, setUserError] = useState('');
  const [userSuccess, setUserSuccess] = useState('');

  // ═══ Config State ═══
  const [config, setConfig] = useState<Record<string, string>>({});
  const [configDraft, setConfigDraft] = useState<Record<string, string>>({});
  const [configSaving, setConfigSaving] = useState(false);
  const [configError, setConfigError] = useState('');
  const [configSuccess, setConfigSuccess] = useState('');

  // Why: GET /manage/users is admin-only on the BE (403 for any other role).
  // Guard here prevents console noise for non-admins; tab visibility is already gated separately.
  const loadUsers = useCallback(async () => {
    if (!canManageUsers) return;
    try { setUsers(await listUsers()); }
    catch (err: any) { setUserError(formatBackendError(err, 'Laden der Benutzer')); }
  }, [canManageUsers]);

  const loadConfig = useCallback(async () => {
    try {
      const cfg = await getCustomerConfig();
      setConfig(cfg);
      setConfigDraft(cfg);
    } catch (err: any) { setConfigError(formatBackendError(err, 'Laden der Konfiguration')); }
  }, []);

  useEffect(() => { loadUsers(); loadConfig(); }, [loadUsers, loadConfig]);

  const handleAddUser = async () => {
    if (!newEmail.trim()) return;
    setUserError(''); setUserSuccess('');
    try {
      await addUser(newEmail.trim(), newIsNormal, newIsAdmin);
      setNewEmail(''); setNewIsNormal(true); setNewIsAdmin(false);
      setUserSuccess(`${newEmail.trim()} hinzugefügt.`);
      await loadUsers();
    } catch (err: any) {
      // Why: 409 is the well-known "E-Mail existiert bereits" case — keep the
      // specific German cause; everything else goes through the structured mapper.
      setUserError(err?.response?.status === 409 ? 'Hinzufügen fehlgeschlagen: E-Mail existiert bereits.' : formatBackendError(err, 'Hinzufügen'));
    }
  };

  const handleDeleteUser = async (id: number) => {
    setUserError(''); setUserSuccess('');
    try {
      await deleteUser(id);
      setDeleteConfirmId(null);
      setUserSuccess('Benutzer entfernt.');
      await loadUsers();
    } catch (err: any) { setUserError(formatBackendError(err, 'Löschen')); }
  };

  const handleToggleRole = async (user: UserEntry, field: 'isNormalUser' | 'isCustomerAdmin') => {
    setUserError('');
    try {
      const isNormal = field === 'isNormalUser' ? !user.isNormalUser : user.isNormalUser;
      const isAdmin = field === 'isCustomerAdmin' ? !user.isCustomerAdmin : user.isCustomerAdmin;
      await updateUserRoles(user.id, isNormal, isAdmin);
      await loadUsers();
    } catch (err: any) { setUserError(formatBackendError(err, 'Aktualisieren')); }
  };

  // Why: separate save handlers per section so a half-finished company-config edit doesn't
  // accidentally ride along when the user toggles a workflow setting (and vice versa).
  const saveDiff = async (predicate: (k: string) => boolean) => {
    setConfigError(''); setConfigSuccess(''); setConfigSaving(true);
    const changed: Record<string, string> = {};
    for (const k of Object.keys(configDraft)) {
      if (predicate(k) && configDraft[k] !== (config[k] ?? '')) changed[k] = configDraft[k];
    }
    if (Object.keys(changed).length === 0) { setConfigSaving(false); return; }
    try {
      await updateCustomerConfig(changed);
      setConfigSuccess(`${Object.keys(changed).length} Einstellung(en) gespeichert.`);
      await loadConfig();
      // Why: App-level state (autoArchiveDays, uiWarps) is sourced from /api/config at
      // mount and stays stale otherwise. Bubble up so AntwortenSeite sees the new threshold
      // without a full page reload.
      onConfigSaved?.();
    } catch (err: any) { setConfigError(formatBackendError(err, 'Speichern')); }
    setConfigSaving(false);
  };
  // Per-section save predicates — keep operator config keys (UI_WARP_*, AUTO_ARCHIVE_DAYS,
  // DISPATCHER_ENABLED, HEADLINE_RATE_MODE) out of the Firmeninformationen save lane.
  const isOperatorConfigKey = (k: string) =>
    k.startsWith('UI_WARP_') || k === 'AUTO_ARCHIVE_DAYS' || k === 'DISPATCHER_ENABLED' || k === 'HEADLINE_RATE_MODE' || k === 'UI_ROW_LAYOUT' || k === 'UI_SHOW_SOURCE_CHANNEL' || k === 'UI_ATTACHMENTS_COUNT_INLINE';

  const handleSaveConfig = () => saveDiff(k => !isOperatorConfigKey(k));
  const handleSaveWorkflow = () => saveDiff(k => k.startsWith('UI_WARP_'));

  const configHasCustomerChanges = Object.keys(configDraft).some(
    k => !isOperatorConfigKey(k) && configDraft[k] !== (config[k] ?? ''));
  const configHasWorkflowChanges = Object.keys(configDraft).some(
    k => k.startsWith('UI_WARP_') && configDraft[k] !== (config[k] ?? ''));

  // Why: the 5 sections below (AutoArchive, Statistik, RowLayout, ShowSourceChannel,
  // AttachmentsCountInline) each govern exactly ONE config key — unlike handleSaveConfig/
  // handleSaveWorkflow above, which own SETS of keys and stay hand-written. Before this factory,
  // a single-key lane repeated its key and its default across four places (handler predicate,
  // dirty check, draft read, Zurücksetzen target); the two default sites only had to disagree
  // once for a never-configured tenant to render off-AND-dirty before anyone touched anything —
  // a real bug this shop already fixed once. singleKeyLane makes each lane declare its key and
  // fallback exactly ONE time, so the two default sites cannot diverge, and a key that doesn't
  // share any neighbour's predicate always gets a lane where it will actually save.
  const singleKeyLane = (key: string, fallback: string) => {
    const draft = configDraft[key] ?? fallback;
    return {
      draft,
      isDirty: draft !== (config[key] ?? fallback),
      save: () => saveDiff(k => k === key),
      reset: () => setConfigDraft(prev => ({ ...prev, [key]: config[key] ?? fallback })),
    };
  };

  const autoArchiveLane = singleKeyLane('AUTO_ARCHIVE_DAYS', '0');
  const statistikLane = singleKeyLane('HEADLINE_RATE_MODE', 'quality_only');
  const roleDefaultRowLayout = defaultRowLayout(role);
  // The axis noun comes from the tenant's own vocabulary, never from its role name — the same
  // seam the ticket surfaces read, so this label and the badges it describes cannot disagree.
  const rowLayoutNoun = useCategoryMeta().categoryNoun;
  const rowLayoutLane = singleKeyLane('UI_ROW_LAYOUT', roleDefaultRowLayout);
  // Fallback 'false' — absent means hidden, here exactly as in /config and api.ts.
  const showSourceChannelLane = singleKeyLane('UI_SHOW_SOURCE_CHANNEL', 'false');
  const showSourceChannelDraft = showSourceChannelLane.draft === 'true';
  // Fallback 'true' — the deliberate inverse of showSourceChannelLane above. Absent here means
  // "nobody has asked for a change", and the unchanged behaviour is counting inline images, so
  // absent reads as ON (never harmonize this to match its neighbour's 'false').
  const attachmentsCountInlineLane = singleKeyLane('UI_ATTACHMENTS_COUNT_INLINE', 'true');
  const attachmentsCountInlineDraft = attachmentsCountInlineLane.draft === 'true';

  // FE-side validation matching BE bounds: non-negative integer.
  const autoArchiveRaw = autoArchiveLane.draft;
  const autoArchiveValid = /^\d+$/.test(autoArchiveRaw);

  const feedbackStyle = (isError: boolean): React.CSSProperties => ({
    fontSize: 12, padding: '6px 12px', borderRadius: theme.borderRadius.sm, marginTop: theme.spacing.sm,
    color: isError ? theme.colors.error : theme.colors.success,
    backgroundColor: isError ? '#fef2f2' : '#f0fdf4',
    border: `1px solid ${isError ? '#fecaca' : '#bbf7d0'}`,
  });

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: theme.spacing.lg, display: 'flex', flexDirection: 'column', gap: theme.spacing.lg }}>
      <h1 className="visually-hidden">Einstellungen</h1>

      {/* Spec §7 — WAI-ARIA tablist + Arrow-key nav. Tabs filtered per role. */}
      <div
        role="tablist"
        aria-label="Einstellungs-Tabs"
        style={{
          display: 'flex', gap: theme.spacing.sm,
          borderBottom: `1px solid ${theme.colors.borderLight}`,
          paddingBottom: theme.spacing.xs,
          // Why: 4-tab row overflows iPhone-SE width (462>375). Horizontal
          // scroll preserves visual hierarchy without forcing a wrap that
          // breaks the underline-active affordance.
          overflowX: 'auto',
          flexWrap: 'nowrap',
        }}
        onKeyDown={e => {
          if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
          e.preventDefault();
          const idx = visibleTabs.indexOf(activeTab);
          const next = e.key === 'ArrowRight'
            ? visibleTabs[(idx + 1) % visibleTabs.length]
            : visibleTabs[(idx - 1 + visibleTabs.length) % visibleTabs.length];
          setActiveTab(next);
          document.getElementById(`einstellungen-tab-${next}`)?.focus();
        }}
      >
        {TAB_DEFS.filter(t => visibleTabs.includes(t.key)).map(t => {
          const active = activeTab === t.key;
          return (
            <button
              key={t.key}
              id={`einstellungen-tab-${t.key}`}
              role="tab"
              aria-selected={active}
              aria-controls={`einstellungen-panel-${t.key}`}
              tabIndex={active ? 0 : -1}
              onClick={() => setActiveTab(t.key)}
              style={{
                padding: '8px 16px', fontSize: 14, fontWeight: active ? 600 : 400,
                fontFamily: theme.font.family,
                color: active ? theme.colors.navy : theme.colors.textSecondary,
                background: 'transparent',
                border: 'none',
                borderBottom: `2px solid ${active ? theme.colors.navy : 'transparent'}`,
                cursor: 'pointer',
                marginBottom: -1,
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      <div
        role="tabpanel"
        id={`einstellungen-panel-${activeTab}`}
        aria-labelledby={`einstellungen-tab-${activeTab}`}
        tabIndex={0}
      >
      {/* ═══ Team & Zugriff (formerly Benutzerverwaltung) — admin only ═══ */}
      {activeTab === 'team' && canManageUsers && (
      <BentoCard title="Benutzerverwaltung">
        <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
          {/* Add user form */}
          <div style={{ display: 'flex', gap: theme.spacing.sm, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              value={newEmail} onChange={e => setNewEmail(e.target.value)}
              placeholder="E-Mail-Adresse" type="email"
              style={{ ...inputStyle, flex: '1 1 280px', minWidth: 200 }}
              onKeyDown={e => e.key === 'Enter' && handleAddUser()}
            />
            <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
              <input type="checkbox" checked={newIsNormal} onChange={e => setNewIsNormal(e.target.checked)} />
              Benutzer
            </label>
            <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
              <input type="checkbox" checked={newIsAdmin} onChange={e => setNewIsAdmin(e.target.checked)} />
              Kundenadmin
            </label>
            <button onClick={handleAddUser} style={btnStyle('primary')}>Hinzufügen</button>
          </div>

          {/* The other half of the no-role screen's instruction: that screen tells the user
              which address to hand over; this line tells the admin to enter exactly that one.
              Always rendered (no conditional collapse) per the layout-stability rule. */}
          <div style={{ fontSize: 12, color: theme.colors.textSecondary, lineHeight: 1.4 }}>
            Genau die Adresse eintragen, die dem Benutzer bei der Anmeldung angezeigt wird
            (Meldung „keine Rolle zugewiesen“) – nicht eine frühere oder alternative Adresse.
          </div>

          {userError && <div style={feedbackStyle(true)}>{userError}</div>}
          {userSuccess && <div style={feedbackStyle(false)}>{userSuccess}</div>}

          {/* User table */}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${theme.colors.bgLight}`, textAlign: 'left' }}>
                <th style={{ padding: '8px 12px', color: theme.colors.textSecondary, fontWeight: 500 }}>E-Mail</th>
                <th style={{ padding: '8px 12px', color: theme.colors.textSecondary, fontWeight: 500 }}>Rollen</th>
                <th style={{ padding: '8px 12px', color: theme.colors.textSecondary, fontWeight: 500, width: 120 }}>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} style={{ borderBottom: `1px solid ${theme.colors.bgLight}` }}>
                  <td style={{ padding: '10px 12px' }}>{u.email}</td>
                  <td style={{ padding: '10px 12px', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span
                      onClick={() => handleToggleRole(u, 'isNormalUser')}
                      style={{ ...badgeStyle(false), cursor: 'pointer', opacity: u.isNormalUser ? 1 : 0.4 }}
                      title="Klicken zum Umschalten"
                    >Benutzer</span>
                    <span
                      onClick={() => handleToggleRole(u, 'isCustomerAdmin')}
                      style={{ ...badgeStyle(true), cursor: 'pointer', opacity: u.isCustomerAdmin ? 1 : 0.4 }}
                      title="Klicken zum Umschalten"
                    >Kundenadmin</span>
                    {u.isAdmin && <span style={badgeStyle(true)}>Admin</span>}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    {deleteConfirmId === u.id ? (
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button onClick={() => handleDeleteUser(u.id)} style={btnStyle('danger')}>Entfernen</button>
                        <button onClick={() => setDeleteConfirmId(null)} style={btnStyle('ghost')}>Abbrechen</button>
                      </div>
                    ) : (
                      <button onClick={() => setDeleteConfirmId(u.id)} style={btnStyle('ghost')}>Entfernen</button>
                    )}
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan={3} style={{ padding: '20px 12px', color: theme.colors.textSecondary, textAlign: 'center' }}>
                  Keine Benutzer gefunden.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </BentoCard>
      )}

      {/* ═══ Workflow tab — UI_WARP toggles + AUTO_ARCHIVE_DAYS ═══ */}
      {activeTab === 'workflow' && (
      <BentoCard title="Workflow & Automatisierung">
        <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.lg }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: theme.colors.navyMid, textTransform: 'uppercase',
                          letterSpacing: 0.5, marginBottom: theme.spacing.sm, paddingBottom: 4,
                          borderBottom: `1px solid ${theme.colors.bgLight}` }}>
              Navigation
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
          {WORKFLOW_TOGGLES
            // Spec §3 — Testgen UI_WARPs hidden for non-Admin (matches Testgeneratorseite
            // admin-only visibility per the "toggle visibility follows page visibility" rule).
            .filter(t => t.key.startsWith('UI_WARP_TESTGEN_') ? !!userRoles?.isAdmin : true)
            .map(({ key, label, description }) => {
            // Why: server-default fallback when key absent (e.g. fresh customer pre-037 sync).
            const fallback = key === 'UI_WARP_APPROVE_TO_GENEHMIGT' ? 'false' : 'true';
            const current = configDraft[key] ?? fallback;
            const checked = current === 'true';
            const dirty = configDraft[key] !== (config[key] ?? fallback);
            return (
              <label key={key} htmlFor={`workflow-${key}`} aria-label={label} style={{
                display: 'flex', alignItems: 'flex-start', gap: theme.spacing.sm, cursor: 'pointer',
                padding: theme.spacing.sm, borderRadius: theme.borderRadius.sm,
                border: `1px solid ${dirty ? theme.colors.warning : 'transparent'}`,
                backgroundColor: theme.colors.bgPage,
              }}>
                <input
                  id={`workflow-${key}`}
                  type="checkbox" checked={checked}
                  onChange={e => setConfigDraft(prev => ({ ...prev, [key]: e.target.checked ? 'true' : 'false' }))}
                  style={{ marginTop: 3 }}
                />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ fontSize: 13, fontWeight: 500, color: theme.colors.navyMid }}>{label}</span>
                  <span style={{ fontSize: 11, color: theme.colors.textSecondary, lineHeight: 1.4 }}>{description}</span>
                </div>
              </label>
            );
          })}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing.sm }}>
            {configHasWorkflowChanges && (
              <button onClick={() => setConfigDraft(prev => {
                const next = { ...prev };
                for (const k of Object.keys(next)) if (k.startsWith('UI_WARP_')) next[k] = config[k] ?? '';
                return next;
              })} style={btnStyle('ghost')}>Zurücksetzen</button>
            )}
            <button
              onClick={handleSaveWorkflow}
              disabled={!configHasWorkflowChanges || configSaving}
              style={{
                ...btnStyle('primary'),
                opacity: (!configHasWorkflowChanges || configSaving) ? 0.5 : 1,
                cursor: (!configHasWorkflowChanges || configSaving) ? 'default' : 'pointer',
              }}
            >{configSaving ? 'Speichern...' : 'Speichern'}</button>
          </div>
            </div>
          </div>

          {/* Auto-Archiv sub-section — was a standalone card pre-2026-05-25 */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: theme.colors.navyMid, textTransform: 'uppercase',
                          letterSpacing: 0.5, marginBottom: theme.spacing.sm, paddingBottom: 4,
                          borderBottom: `1px solid ${theme.colors.bgLight}` }}>
              Auto-Archiv
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
          <label htmlFor="auto-archive-days" style={{ fontSize: 13, fontWeight: 500, color: theme.colors.navyMid }}>
            Tage bis Auto-Archiv
          </label>
          <input
            id="auto-archive-days"
            type="number" min={0} step={1}
            value={autoArchiveLane.draft}
            onChange={e => setConfigDraft(prev => ({ ...prev, AUTO_ARCHIVE_DAYS: e.target.value }))}
            disabled={!canEditAutoArchive}
            style={{
              maxWidth: 120,
              padding: '6px 10px', fontSize: 13,
              fontFamily: theme.font.family,
              border: `1px solid ${autoArchiveLane.isDirty && autoArchiveValid ? theme.colors.warning : theme.colors.borderLight}`,
              borderRadius: theme.borderRadius.sm,
              outline: 'none',
              opacity: canEditAutoArchive ? 1 : 0.7,
              cursor: canEditAutoArchive ? 'text' : 'default',
            }}
          />
          <span style={{ fontSize: 11, color: theme.colors.textSecondary, lineHeight: 1.4 }}>
            0 = deaktiviert. Wenn &gt; 0: Auto-Button auf Antworten archiviert Tickets älter als N Tage (ohne Auswahl).
          </span>
          {!autoArchiveValid && (
            <span style={{ fontSize: 11, color: theme.colors.error }}>
              Bitte eine nicht-negative ganze Zahl eingeben.
            </span>
          )}
          {canEditAutoArchive && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing.sm }}>
            {autoArchiveLane.isDirty && (
              <button
                onClick={autoArchiveLane.reset}
                style={btnStyle('ghost')}
              >Zurücksetzen</button>
            )}
            <button
              onClick={autoArchiveLane.save}
              disabled={!autoArchiveLane.isDirty || !autoArchiveValid || configSaving}
              style={{
                ...btnStyle('primary'),
                opacity: (!autoArchiveLane.isDirty || !autoArchiveValid || configSaving) ? 0.5 : 1,
                cursor: (!autoArchiveLane.isDirty || !autoArchiveValid || configSaving) ? 'default' : 'pointer',
              }}
            >{configSaving ? 'Speichern...' : 'Speichern'}</button>
          </div>
          )}
            </div>
          </div>

          {/* Darstellung — per-user display preferences (FE-only, localStorage) */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: theme.colors.navyMid, textTransform: 'uppercase',
                          letterSpacing: 0.5, marginBottom: theme.spacing.sm, paddingBottom: 4,
                          borderBottom: `1px solid ${theme.colors.bgLight}` }}>
              Darstellung
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
                <span style={{ fontSize: 13, fontWeight: 500, color: theme.colors.navyMid }}>Farbschema</span>
                <DarstellungToggle />
                <span style={{ fontSize: 11, color: theme.colors.textSecondary, lineHeight: 1.4 }}>
                  Farbschema für die Neuanalysen- und Archiv-Ansichten
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
                <span style={{ fontSize: 13, fontWeight: 500, color: theme.colors.navyMid }}>Detailansicht vergrößern bei Auswahl</span>
                <ListDetailFlipToggle />
                <span style={{ fontSize: 11, color: theme.colors.textSecondary, lineHeight: 1.4 }}>
                  Wenn aktiv, schrumpft die Ticketliste beim Öffnen eines Tickets, damit das Detail mehr Platz bekommt. Standard: Aus (gleichbleibende Aufteilung).
                </span>
              </div>
            </div>
          </div>

          {/* Detailansicht — tenant display settings for the ticket DETAIL pane.
              ⚠ Named for the SURFACE it governs, not "Anzeige": this tab already carries a
              "Darstellung" (per-user, localStorage) and a "Darstellung Ticket-Zeile" (the ROW), and a
              third heading meaning "display" would leave three sections no one can tell apart. The
              detail pane is a distinct surface, so it gets a distinct section — and the next detail
              toggle has an obvious home. */}
          {canEditAutoArchive && (
          <div data-testid="detailansicht-section">
            <div style={{ fontSize: 12, fontWeight: 600, color: theme.colors.navyMid, textTransform: 'uppercase',
                          letterSpacing: 0.5, marginBottom: theme.spacing.sm, paddingBottom: 4,
                          borderBottom: `1px solid ${theme.colors.bgLight}` }}>
              Detailansicht
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
              {/* Own data-testid + aria-labelled Speichern/Zurücksetzen: two sub-controls now share
                  this section, and an un-qualified "Speichern" name is ambiguous to a screen-reader
                  user exactly as it was ambiguous to an unscoped test query — same defect, two costs. */}
              <div data-testid="eingangsweg-subsection">
              {/* aria-label mirrors the Navigation toggles above: the visible text lives in nested
                  spans, which the build's a11y lint does not accept as the label's accessible name. */}
              <label
                htmlFor="ui-show-source-channel"
                aria-label="Eingangsweg im Vorgangsdetail anzeigen"
                style={{ display: 'flex', alignItems: 'flex-start', gap: theme.spacing.sm, cursor: 'pointer' }}
              >
                <input
                  id="ui-show-source-channel"
                  type="checkbox"
                  checked={showSourceChannelDraft}
                  onChange={e => setConfigDraft(prev => ({
                    ...prev, UI_SHOW_SOURCE_CHANNEL: e.target.checked ? 'true' : 'false',
                  }))}
                  style={{ marginTop: 2, cursor: 'pointer' }}
                />
                <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ fontSize: 13, fontWeight: 500, color: theme.colors.navyMid }}>
                    Eingangsweg im Vorgangsdetail anzeigen
                  </span>
                  <span style={{ fontSize: 11, color: theme.colors.textSecondary, lineHeight: 1.4 }}>
                    Zeigt im Vorgangsdetail zusätzlich an, auf welchem Weg der Vorgang eingegangen ist
                    (lokal hochgeladen oder aus Outlook weitergeleitet). Standard: Aus.
                  </span>
                </span>
              </label>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing.sm }}>
                {showSourceChannelLane.isDirty && (
                  <button
                    aria-label="Zurücksetzen (Eingangsweg)"
                    onClick={showSourceChannelLane.reset}
                    style={btnStyle('ghost')}
                  >Zurücksetzen</button>
                )}
                <button
                  aria-label="Speichern (Eingangsweg)"
                  onClick={showSourceChannelLane.save}
                  disabled={!showSourceChannelLane.isDirty || configSaving}
                  style={{
                    ...btnStyle('primary'),
                    opacity: (!showSourceChannelLane.isDirty || configSaving) ? 0.5 : 1,
                    cursor: (!showSourceChannelLane.isDirty || configSaving) ? 'default' : 'pointer',
                  }}
                >{configSaving ? 'Speichern...' : 'Speichern'}</button>
              </div>
              </div>

              <div data-testid="attachments-count-inline-subsection">
              <label
                htmlFor="ui-attachments-count-inline"
                aria-label="Eingebettete Bilder als Anhänge zählen"
                style={{ display: 'flex', alignItems: 'flex-start', gap: theme.spacing.sm, cursor: 'pointer' }}
              >
                <input
                  id="ui-attachments-count-inline"
                  type="checkbox"
                  checked={attachmentsCountInlineDraft}
                  onChange={e => setConfigDraft(prev => ({
                    ...prev, UI_ATTACHMENTS_COUNT_INLINE: e.target.checked ? 'true' : 'false',
                  }))}
                  style={{ marginTop: 2, cursor: 'pointer' }}
                />
                <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ fontSize: 13, fontWeight: 500, color: theme.colors.navyMid }}>
                    Eingebettete Bilder als Anhänge zählen
                  </span>
                  <span style={{ fontSize: 11, color: theme.colors.textSecondary, lineHeight: 1.4 }}>
                    Signaturlogos und andere eingebettete Bilder zählen in der Anhangsanzeige mit. Ist die
                    Option deaktiviert, zählt die Überschrift nur Dokumente; eingebettete Bilder werden
                    weiterhin ausgewiesen. Standard: An.
                  </span>
                </span>
              </label>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing.sm }}>
                {attachmentsCountInlineLane.isDirty && (
                  <button
                    aria-label="Zurücksetzen (Eingebettete Bilder)"
                    onClick={attachmentsCountInlineLane.reset}
                    style={btnStyle('ghost')}
                  >Zurücksetzen</button>
                )}
                <button
                  aria-label="Speichern (Eingebettete Bilder)"
                  onClick={attachmentsCountInlineLane.save}
                  disabled={!attachmentsCountInlineLane.isDirty || configSaving}
                  style={{
                    ...btnStyle('primary'),
                    opacity: (!attachmentsCountInlineLane.isDirty || configSaving) ? 0.5 : 1,
                    cursor: (!attachmentsCountInlineLane.isDirty || configSaving) ? 'default' : 'pointer',
                  }}
                >{configSaving ? 'Speichern...' : 'Speichern'}</button>
              </div>
              </div>
            </div>
          </div>
          )}

          {/* Ticket-row category display — admin/customerAdmin only (save persists to customer_config) */}
          {canEditAutoArchive && (
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: theme.colors.navyMid, textTransform: 'uppercase',
                          letterSpacing: 0.5, marginBottom: theme.spacing.sm, paddingBottom: 4,
                          borderBottom: `1px solid ${theme.colors.bgLight}` }}>
              Darstellung Ticket-Zeile
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
              <div style={{
                padding: theme.spacing.sm, borderRadius: theme.borderRadius.sm,
                border: `1px solid ${rowLayoutLane.isDirty ? theme.colors.warning : 'transparent'}`,
                backgroundColor: theme.colors.bgPage,
              }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: theme.colors.navyMid, marginBottom: 8, fontFamily: theme.font.family }}>
                  {rowLayoutNoun}-Anzeige
                </div>
                <RadioGroup
                  name="UI_ROW_LAYOUT"
                  options={[
                    { value: 'strip_label', label: 'Beschriftung rechts', isDefault: roleDefaultRowLayout === 'strip_label' },
                    { value: 'strip_abbr', label: 'Kürzel im Streifen', isDefault: roleDefaultRowLayout === 'strip_abbr' },
                  ]}
                  value={rowLayoutLane.draft}
                  onChange={v => setConfigDraft(d => ({ ...d, UI_ROW_LAYOUT: v }))}
                />
                <p style={{ fontSize: 11, color: theme.colors.textSecondary, marginTop: 8, lineHeight: 1.4, fontFamily: theme.font.family, margin: '8px 0 0' }}>
                  {rowLayoutLane.draft === 'strip_label'
                    ? `${rowLayoutNoun}-Beschriftung als Badge in Zeile 2`
                    : 'Kurzkürzel im farbigen Streifen'}
                </p>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing.sm }}>
                {rowLayoutLane.isDirty && (
                  <button
                    onClick={rowLayoutLane.reset}
                    style={btnStyle('ghost')}
                  >Zurücksetzen</button>
                )}
                <button
                  onClick={rowLayoutLane.save}
                  disabled={!rowLayoutLane.isDirty || configSaving}
                  style={{
                    ...btnStyle('primary'),
                    opacity: (!rowLayoutLane.isDirty || configSaving) ? 0.5 : 1,
                    cursor: (!rowLayoutLane.isDirty || configSaving) ? 'default' : 'pointer',
                  }}
                >{configSaving ? 'Speichern...' : 'Speichern'}</button>
              </div>
            </div>
          </div>
          )}
        </div>
      </BentoCard>

      )}

      {/* ═══ Statistik-Modus tab — HEADLINE_RATE_MODE radio ═══ */}
      {activeTab === 'statistik' && (
      <BentoCard title="Statistik">
        <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
          {(() => {
            const statistikGroup = CONFIG_GROUPS.find(g => g.label === 'Statistik');
            if (!statistikGroup) return null;
            return statistikGroup.keys.map(entry => {
              const defaultVal = entry.options?.find(o => o.isDefault)?.value ?? '';
              const currentVal = configDraft[entry.key] ?? defaultVal;
              const dirty = currentVal !== (config[entry.key] ?? defaultVal);
              return (
                <div key={entry.key} style={{
                  padding: theme.spacing.sm, borderRadius: theme.borderRadius.sm,
                  border: `1px solid ${dirty ? theme.colors.warning : 'transparent'}`,
                  backgroundColor: theme.colors.bgPage,
                }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: theme.colors.navyMid, marginBottom: 8, fontFamily: theme.font.family }}>
                    {entry.label}
                  </div>
                  {entry.type === 'radio' && entry.options ? (
                    <RadioGroup
                      name={entry.key}
                      options={entry.options}
                      value={currentVal}
                      onChange={v => setConfigDraft(d => ({ ...d, [entry.key]: v }))}
                    />
                  ) : (
                    <input
                      type="text"
                      value={currentVal}
                      onChange={e => setConfigDraft(d => ({ ...d, [entry.key]: e.target.value }))}
                      style={inputStyle}
                    />
                  )}
                  {entry.description && (
                    <p style={{ fontSize: 11, color: theme.colors.textSecondary, marginTop: 8, lineHeight: 1.4, fontFamily: theme.font.family, margin: '8px 0 0' }}>
                      {entry.description}
                    </p>
                  )}
                </div>
              );
            });
          })()}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing.sm }}>
            {statistikLane.isDirty && (
              <button
                onClick={statistikLane.reset}
                style={btnStyle('ghost')}
              >Zurücksetzen</button>
            )}
            <button
              onClick={statistikLane.save}
              disabled={!statistikLane.isDirty || configSaving}
              style={{
                ...btnStyle('primary'),
                opacity: (!statistikLane.isDirty || configSaving) ? 0.5 : 1,
                cursor: (!statistikLane.isDirty || configSaving) ? 'default' : 'pointer',
              }}
            >{configSaving ? 'Speichern...' : 'Speichern'}</button>
          </div>
        </div>
      </BentoCard>

      )}

      {/* ═══ Firmendaten tab — Customer config (5 sub-groups) ═══ */}
      {activeTab === 'firma' && (
      <BentoCard title="Firmeninformationen">
        {/* Spec §6 — read-only mode badge for Sachbearbeiter (canEditFirma=false). */}
        {!canEditFirma && (
          <div style={{
            fontSize: 12, color: theme.colors.textSecondary,
            background: theme.colors.bgLight,
            padding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
            borderRadius: theme.borderRadius.sm,
            marginBottom: theme.spacing.md,
            fontStyle: 'italic',
          }}>
            Nur Lese-Zugriff — bitte Administrator kontaktieren, um Werte zu ändern.
          </div>
        )}
        {(() => {
          const renderField = (key: string, label: string) => (
            <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <label style={{ fontSize: 11, color: theme.colors.textSecondary, fontWeight: 500 }}>{label}</label>
              <input
                value={configDraft[key] ?? ''}
                onChange={e => setConfigDraft(prev => ({ ...prev, [key]: e.target.value }))}
                disabled={!canEditFirma}
                style={{
                  ...inputStyle,
                  borderColor: configDraft[key] !== (config[key] ?? '') ? theme.colors.warning : theme.colors.borderLight,
                  opacity: canEditFirma ? 1 : 0.7,
                  cursor: canEditFirma ? 'text' : 'default',
                }}
                placeholder={`{${key}}`}
              />
            </div>
          );

          const groupHeaderStyle: React.CSSProperties = {
            fontSize: 12, fontWeight: 600, color: theme.colors.navyMid, textTransform: 'uppercase',
            letterSpacing: 0.5, marginBottom: theme.spacing.sm, paddingBottom: 4,
            borderBottom: `1px solid ${theme.colors.bgLight}`,
          };

          const gridStyle: React.CSSProperties = {
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: theme.spacing.sm,
          };

          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.lg }}>
              {/* Statistik group is excluded here — it has its own dedicated card above
                  (HEADLINE_RATE_MODE doesn't belong in the Firmeninformationen save-lane). */}
              {CONFIG_GROUPS.filter(g => g.label !== 'Statistik').map(group => {
                const visibleKeys = group.keys.filter(k => !k.roles || (k.roles as readonly string[]).includes(role));
                if (visibleKeys.length === 0) return null;
                return (
                  <div key={group.label}>
                    <div style={groupHeaderStyle}>{group.label}</div>
                    <div style={gridStyle}>
                      {visibleKeys.map(({ key, label }) => renderField(key, label))}
                    </div>
                  </div>
                );
              })}

              {configError && <div style={feedbackStyle(true)}>{configError}</div>}
              {configSuccess && <div style={feedbackStyle(false)}>{configSuccess}</div>}

              {canEditFirma && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing.sm }}>
                {configHasCustomerChanges && (
                  <button onClick={() => setConfigDraft(prev => {
                    const next = { ...prev };
                    for (const k of Object.keys(next)) if (!k.startsWith('UI_WARP_')) next[k] = config[k] ?? '';
                    return next;
                  })} style={btnStyle('ghost')}>Zurücksetzen</button>
                )}
                <button
                  onClick={handleSaveConfig}
                  disabled={!configHasCustomerChanges || configSaving}
                  style={{
                    ...btnStyle('primary'),
                    opacity: (!configHasCustomerChanges || configSaving) ? 0.5 : 1,
                    cursor: (!configHasCustomerChanges || configSaving) ? 'default' : 'pointer',
                  }}
                >{configSaving ? 'Speichern...' : 'Speichern'}</button>
              </div>
              )}
            </div>
          );
        })()}
      </BentoCard>
      )}

      {/* ═══ Texte & Vokabular tab — the generalized editing surface (admin only) ═══ */}
      {activeTab === 'texte' && canManageUsers && (
        // Frozen prose cards (e.g. Notfall) link back to Firmendaten for the Notfall-Rufnummer —
        // reuse the existing in-page tab-routing (same mechanism resolveInitialTab reads from
        // ?tab=) rather than a hard navigation.
        <TexteUndVokabular role={role} onNavigateToFirmendaten={() => setActiveTab('firma')} />
      )}

      {/* ═══ Antwort-Katalog tab — read-only capability catalog mirror (admin only) ═══ */}
      {activeTab === 'katalog' && canManageUsers && <AntwortKatalog />}

      {/* ═══ Routing tab — routing_rules CRUD (admin only) ═══ */}
      {activeTab === 'routing' && canManageUsers && <RoutingVerwaltung />}
      </div>
    </div>
  );
};

export default EinstellungenSeite;
